import React, { useState, useEffect } from "react";
import {
  Layout,
  Space,
  Button,
  Card,
  Alert,
  Spin,
  Typography,
  Row,
  Col,
  Tag,
  Table,
  Statistic,
  message,
  Modal,
  Select,
  InputNumber,
  Divider,
  Steps,
  Result,
  Badge,
  Descriptions,
  Empty,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  SafetyOutlined,
  SettingOutlined,
  SyncOutlined,
  WalletOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { getApi, postApi, putApi, moneyFormat } from "@utils";
import { Header } from "@components";
import moment from "moment";
import { ConsignorTransferUI } from "./ConsignorTransferUI";

const { Content } = Layout;
const { Title, Text, Link } = Typography;
const { Step } = Steps;
const { Option } = Select;

// Main Stripe Settings Component
export const StripeSettings = () => {
  const [loading, setLoading] = useState(true);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [payoutSettings, setPayoutSettings] = useState({
    payout_schedule: "manual",
    payout_minimum_amount: 0,
  });
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);

  useEffect(() => {
    fetchStripeData();
  }, []);

  const fetchStripeData = async () => {
    setLoading(true);
    try {
      const response = await getApi("getStripeConnect", {});
      if (response?.data) {
        setStripeAccount(response.data.stripe_account);
        setPendingPayouts(response.data.pending_payouts || []);
        setRecentTransfers(response.data.recent_transfers || []);
        if (response.data.stripe_account) {
          setPayoutSettings({
            payout_schedule: response.data.payout_schedule || "manual",
            payout_minimum_amount: response.data.payout_minimum_amount || 0,
          });
        }
      }
    } catch (error) {
      message.error("Failed to load Stripe account information");
    }
    setLoading(false);
  };

  const handleConnectStripe = async () => {
    setLoading(true);
    try {
      const response = await postApi("postStripeConnect", {
        business_type: "individual",
      });
      if (response?.data?.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      message.error("Failed to create Stripe account");
    }
    setLoading(false);
  };

  const handleCompleteOnboarding = async () => {
    setLoading(true);
    try {
      const response = await postApi("postStripeConnectOnboarding", {});
      if (response?.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      message.error("Failed to generate onboarding link");
    }
    setLoading(false);
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await postApi("postStripeConnectDashboard", {});
      if (response?.data?.url) {
        window.open(response.data.url, "_blank");
      }
    } catch (error) {
      message.error("Failed to open Stripe dashboard");
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await putApi("putStripeConnectSettings", payoutSettings);
      message.success("Payout settings updated successfully");
      setSettingsModalVisible(false);
      fetchStripeData();
    } catch (error) {
      message.error("Failed to update settings");
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      enabled: { color: "success", text: "Active" },
      pending: { color: "warning", text: "Pending" },
      restricted: { color: "error", text: "Restricted" },
      deauthorized: { color: "default", text: "Disconnected" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const transferColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      render: (date) => moment(date).format("MMM DD, YYYY"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount) => moneyFormat(amount),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        const statusConfig = {
          pending: { color: "orange", icon: <ClockCircleOutlined /> },
          paid: { color: "green", icon: <CheckCircleOutlined /> },
          failed: { color: "red", icon: <ExclamationCircleOutlined /> },
        };
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: "Available",
      dataIndex: "available_on",
      render: (date) => (date ? moment(date).format("MMM DD, YYYY") : "-"),
    },
  ];

  if (loading) {
    return (
      <Space
        direction="vertical"
        style={{ width: "100%", backgroundColor: "white" }}
      >
        <Header selected="stripe_settings" />
        <Content style={{ padding: "50px", textAlign: "center" }}>
          <Spin size="large" />
        </Content>
      </Space>
    );
  }

  // Not connected to Stripe
  if (!stripeAccount) {
    return (
      <Space
        direction="vertical"
        style={{ width: "100%", backgroundColor: "white" }}
      >
        <Header selected="stripe_settings" />
        <Content style={{ padding: "24px" }}>
          <Row justify="center">
            <Col xs={24} sm={20} md={16} lg={12}>
              <Card>
                <Result
                  icon={<WalletOutlined style={{ color: "#6772e5" }} />}
                  title="Connect Your Stripe Account"
                  subTitle="Set up your Stripe account to receive automatic payouts for your sold items"
                  extra={[
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleConnectStripe}
                      loading={loading}
                      icon={<BankOutlined />}
                    >
                      Connect with Stripe
                    </Button>,
                  ]}
                />
                <Divider />
                <Title level={5}>Why Connect Stripe?</Title>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text>
                    <CheckCircleOutlined
                      style={{ color: "#52c41a", marginRight: 8 }}
                    />
                    Receive automatic payouts when items sell
                  </Text>
                  <Text>
                    <CheckCircleOutlined
                      style={{ color: "#52c41a", marginRight: 8 }}
                    />
                    Track your earnings in real-time
                  </Text>
                  <Text>
                    <CheckCircleOutlined
                      style={{ color: "#52c41a", marginRight: 8 }}
                    />
                    Secure and encrypted transactions
                  </Text>
                  <Text>
                    <CheckCircleOutlined
                      style={{ color: "#52c41a", marginRight: 8 }}
                    />
                    Direct deposit to your bank account
                  </Text>
                </Space>
              </Card>
            </Col>
          </Row>
        </Content>
      </Space>
    );
  }

  // Onboarding incomplete
  if (!stripeAccount.fully_onboarded) {
    const currentStep = stripeAccount.details_submitted ? 1 : 0;

    return (
      <Space
        direction="vertical"
        style={{ width: "100%", backgroundColor: "white" }}
      >
        <Header selected="stripe_settings" />
        <Content style={{ padding: "24px" }}>
          <Row justify="center">
            <Col xs={24} sm={20} md={16} lg={12}>
              <Card>
                <Title level={4}>Complete Your Stripe Setup</Title>
                <Steps current={currentStep} style={{ marginBottom: 24 }}>
                  <Step
                    title="Account Created"
                    icon={<CheckCircleOutlined />}
                  />
                  <Step
                    title="Complete Verification"
                    icon={currentStep === 1 ? <LoadingOutlined /> : null}
                  />
                  <Step title="Ready for Payouts" />
                </Steps>

                <Alert
                  message="Action Required"
                  description="Please complete your Stripe account setup to start receiving payouts."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                {stripeAccount.pending_requirements?.length > 0 && (
                  <Alert
                    message="Pending Requirements"
                    description={
                      <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                        {stripeAccount.pending_requirements.map((req, idx) => (
                          <li key={idx}>
                            {req
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </li>
                        ))}
                      </ul>
                    }
                    type="info"
                    style={{ marginBottom: 24 }}
                  />
                )}

                <Button
                  type="primary"
                  size="large"
                  onClick={handleCompleteOnboarding}
                  loading={loading}
                  block
                >
                  Complete Setup
                </Button>
              </Card>
            </Col>
          </Row>
        </Content>
      </Space>
    );
  }

  // Fully connected and onboarded
  return (
    <Space
      direction="vertical"
      style={{ width: "100%", backgroundColor: "white" }}
    >
      <Header selected="stripe_settings" />
      <Content style={{ padding: "24px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Title level={4}>
                  <WalletOutlined /> Stripe Account
                </Title>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Status">
                    {getStatusTag(stripeAccount.status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Account Type">
                    {stripeAccount.account_type.charAt(0).toUpperCase() +
                      stripeAccount.account_type.slice(1)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country">
                    {stripeAccount.country}
                  </Descriptions.Item>
                  <Descriptions.Item label="Currency">
                    {(stripeAccount.default_currency || "USD").toUpperCase()}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Space style={{ width: "100%" }} direction="vertical">
                  <Button
                    onClick={handleOpenDashboard}
                    icon={<SettingOutlined />}
                    block
                  >
                    Open Stripe Dashboard
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Card>
                  <Statistic
                    title="Pending Payouts"
                    value={pendingPayouts.length}
                    prefix={<ClockCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Statistic
                    title="Pending Amount"
                    value={pendingPayouts.reduce(
                      (sum, p) => sum + parseFloat(p.cashout_amount || 0),
                      0
                    )}
                    prefix="$"
                    precision={2}
                  />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Transferred"
                    value={recentTransfers
                      .filter((t) => t.status === "paid")
                      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)}
                    prefix="$"
                    precision={2}
                  />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Statistic
                    title="Payout Schedule"
                    value={
                      payoutSettings.payout_schedule.charAt(0).toUpperCase() +
                      payoutSettings.payout_schedule.slice(1)
                    }
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Card>
              </Col>
            </Row>

            <Card style={{ marginTop: 16 }}>
              <Title level={5}>Recent Transfers</Title>
              {recentTransfers.length > 0 ? (
                <Table
                  dataSource={recentTransfers}
                  columns={transferColumns}
                  pagination={false}
                  size="small"
                  rowKey="id"
                />
              ) : (
                <Empty description="No transfers yet" />
              )}
            </Card>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <ConsignorTransferUI />
          </Col>
        </Row>

        <Card style={{ marginTop: 16 }}>
          <Title level={5}>Recent Transfers</Title>
          {recentTransfers.length > 0 ? (
            <Table
              dataSource={recentTransfers}
              columns={transferColumns}
              pagination={false}
              size="small"
              rowKey="id"
            />
          ) : (
            <Empty description="No transfers yet" />
          )}
        </Card>

        <Modal
          title="Payout Settings"
          visible={settingsModalVisible}
          onOk={handleUpdateSettings}
          onCancel={() => setSettingsModalVisible(false)}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            {/* <div>
              <Text strong>Payout Schedule</Text>
              <Select
                value={payoutSettings.payout_schedule}
                onChange={(value) =>
                  setPayoutSettings({
                    ...payoutSettings,
                    payout_schedule: value,
                  })
                }
                style={{ width: "100%", marginTop: 8 }}
              >
                <Option value="manual">Manual</Option>
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
              </Select>
              <Text type="secondary" style={{ fontSize: 12 }}>
                How often payouts are automatically processed
              </Text>
            </div> */}

            <div>
              <Text strong>Minimum Payout Amount</Text>
              <InputNumber
                value={payoutSettings.payout_minimum_amount}
                onChange={(value) =>
                  setPayoutSettings({
                    ...payoutSettings,
                    payout_minimum_amount: value,
                  })
                }
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => Number(value.replace(/\$\s?|(,*)/g, ""))}
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                step={10}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Payouts will only be sent when balance exceeds this amount
              </Text>
            </div>
          </Space>
        </Modal>
      </Content>
    </Space>
  );
};

// Payout Action Component for Admin
export const PayoutActions = ({ payout, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);

  const handleCreateTransfer = async () => {
    setLoading(true);
    try {
      const response = await postApi("postStripeTransfer", {
        payout_id: payout.id,
      });
      message.success("Transfer created successfully");
      setTransferModalVisible(false);
      if (onComplete) onComplete();
    } catch (error) {
      message.error(error.message || "Failed to create transfer");
    }
    setLoading(false);
  };

  const canTransfer =
    payout.status === "confirmed" &&
    !payout.transfer_status &&
    payout.cashout_amount > 0;

  if (!canTransfer) {
    if (payout.transfer_status === "completed") {
      return (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Transferred
        </Tag>
      );
    }
    if (payout.transfer_status === "failed") {
      return (
        <Tag color="error" icon={<ExclamationCircleOutlined />}>
          Transfer Failed
        </Tag>
      );
    }
    if (payout.transfer_status === "pending") {
      return (
        <Tag color="processing" icon={<SyncOutlined spin />}>
          Processing
        </Tag>
      );
    }
    return null;
  }

  return (
    <>
      <Button
        type="primary"
        size="small"
        onClick={() => setTransferModalVisible(true)}
        icon={<DollarOutlined />}
      >
        Create Transfer
      </Button>

      <Modal
        title="Create Stripe Transfer"
        visible={transferModalVisible}
        onOk={handleCreateTransfer}
        onCancel={() => setTransferModalVisible(false)}
        confirmLoading={loading}
      >
        <Alert
          message="Confirm Transfer"
          description={`This will transfer ${moneyFormat(
            payout.cashout_amount
          )} to the consignor's Stripe account.`}
          type="info"
          showIcon
        />
        <Descriptions column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Payout ID">{payout.id}</Descriptions.Item>
          <Descriptions.Item label="Amount">
            {moneyFormat(payout.cashout_amount)}
          </Descriptions.Item>
          <Descriptions.Item label="Product">
            {payout.product_title}
          </Descriptions.Item>
          <Descriptions.Item label="Sold Date">
            {moment(payout.sold_at).format("MMM DD, YYYY")}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </>
  );
};
